import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { WIDGET_HOST_CHANGED_EVENT } from 'utils/widgets/widgetHost.utils';

const INTERACTION_EVENTS = ['mousemove', 'mouseup', 'touchmove', 'touchend', 'touchcancel'];

/**
 * Leaflet 1.x captures the document that loaded the module. When an existing
 * map container is adopted by a pop-out document, drag completion and keyboard
 * events would otherwise continue listening on the dashboard document.
 *
 * Keep one Leaflet instance and bridge only the document-level events while the
 * map is hosted elsewhere. Container-level click, wheel, touch, and layer
 * handlers continue to work normally without duplicate listeners.
 */
const MapCrossDocumentInteractionFix = () => {
  const map = useMap();

  useEffect(() => {
    if (typeof map?.getContainer !== 'function') return undefined;

    const container = map.getContainer();
    let hostedDocument = null;
    let isPointerInteractionActive = false;

    const handlePointerStart = () => {
      isPointerInteractionActive = true;
    };

    const handlePointerInteraction = (event) => {
      if (!isPointerInteractionActive) return;

      const draggable = map.dragging?._draggable;
      if (!draggable) return;

      if (event.type === 'mousemove' || event.type === 'touchmove') {
        draggable._onMove?.(event);
        return;
      }

      draggable._onUp?.(event);
      isPointerInteractionActive = false;
    };

    const handleKeyDown = (event) => {
      if (map.keyboard?.enabled?.()) {
        map.keyboard._onKeyDown?.(event);
      }
    };

    const unbindHostedDocument = () => {
      if (!hostedDocument) return;
      INTERACTION_EVENTS.forEach((eventName) => {
        hostedDocument.removeEventListener(eventName, handlePointerInteraction);
      });
      hostedDocument.removeEventListener('keydown', handleKeyDown);
      hostedDocument = null;
    };

    const bindHostedDocument = () => {
      unbindHostedDocument();

      const nextDocument = container.ownerDocument;
      if (!nextDocument || nextDocument === document) return;

      hostedDocument = nextDocument;
      INTERACTION_EVENTS.forEach((eventName) => {
        hostedDocument.addEventListener(eventName, handlePointerInteraction, {
          passive: eventName !== 'touchmove',
        });
      });
      hostedDocument.addEventListener('keydown', handleKeyDown);
    };

    container.addEventListener(WIDGET_HOST_CHANGED_EVENT, bindHostedDocument);
    container.addEventListener('mousedown', handlePointerStart);
    container.addEventListener('touchstart', handlePointerStart, { passive: true });
    container.dataset.widgetHostAware = '';
    bindHostedDocument();

    return () => {
      container.removeEventListener(WIDGET_HOST_CHANGED_EVENT, bindHostedDocument);
      container.removeEventListener('mousedown', handlePointerStart);
      container.removeEventListener('touchstart', handlePointerStart);
      delete container.dataset.widgetHostAware;
      unbindHostedDocument();
    };
  }, [map]);

  return null;
};

export default MapCrossDocumentInteractionFix;
